import { isEqual } from "lodash";
import { DataSchema } from "./data/DataManager";
import { ValidationSchema } from "./validation/ValidationManager";
import { FormSchema } from "./form/FormManager";
import { BasicState } from "./common/DynamicManager";
import { DataTransformSchema } from "./data/DataTransformManager";
import { GraphqlSchema } from "./graphql/GraphqlManager";
import { DisplaySchema } from "./display/DisplayManager";
import { GridSchema } from "./grid/AgGridManager";
import { CsvSchema } from "./csv/CsvManager";

export type FieldName = string;

export type FieldNames = FieldName[];

export type FieldValueSimple = undefined | null | boolean | string | number;

export type FieldValue = FieldValueSimple | FieldValueObject | FieldValueArray;

export interface FieldValueArray<
  T extends FieldValue = FieldValue
> extends Array<T> {}

export interface FieldValueObject<
  T extends FieldValue = FieldValue
> extends Record<string, T> {}

export type FieldValues<T extends FieldNames = FieldNames> = Partial<Record<T[number], FieldValue>>;

export const EMPTY_VALUES: FieldValue[] = [undefined, null, ""];

export const EMPTY_VALUE = null;

export type FieldsSubscriptions = Record<string, string[]>;

export type DynamicStructureGetter<T> = (values: FieldValues) => (Promise<T> | T);

export interface DynamicStructure<T, F extends FieldNames = FieldNames> {
  getter: DynamicStructureGetter<T>;
  fields?: F;
  cache?: {
    fieldValues: Record<F[number], FieldValue>,
    cachedResult: T,
  },
  disabledCache?: boolean,
}

export interface PreparedDynamicStructure<
  T,
  F extends FieldNames = FieldNames
> extends DynamicStructure<T, F> {
  fields: F;
}

export type DynamicPropertyType<
  T,
  F extends FieldNames = FieldNames
> = T | (() => T) | DynamicStructure<T, F>;

export type Dynamic<T> = {
  [K in keyof T]: DynamicPropertyType<T[K]>
};

export type Generator<T extends BasicState> = {
  [K in keyof T]: PreparedDynamicStructure<T[K]>
}

export type StateGenerator<
  S extends BasicState,
  K extends FieldNames = FieldNames
> = Record<K[number], Generator<S>>;

export enum SchemaAspects {
  DATA = "data",
  DATA_TRANSFORM = "dataTransform",
  VALIDATION = "validation",
  FORM = "form",
  GRAPHQL = "graphql",
  DISPLAY = "display",
  GRID = "grid",
  CSV = "csv",
}

export interface SchemaAspectsMapping {
  [SchemaAspects.DATA]: Dynamic<DataSchema>,
  [SchemaAspects.DATA_TRANSFORM]: Dynamic<DataTransformSchema>,
  [SchemaAspects.VALIDATION]: Dynamic<ValidationSchema>,
  [SchemaAspects.FORM]: Dynamic<FormSchema>,
  [SchemaAspects.GRAPHQL]: Dynamic<GraphqlSchema>,
  [SchemaAspects.DISPLAY]: Dynamic<DisplaySchema>,
  [SchemaAspects.GRID]: Dynamic<GridSchema>,
  [SchemaAspects.CSV]: Dynamic<CsvSchema>,
}

export type FieldSchema = {
    name: FieldName,
  } &
  {
    [A in SchemaAspects]?: SchemaAspectsMapping[A];
  }

export type Schema<K extends FieldNames = FieldNames> = Record<K[number], FieldSchema>

export const prepareValues = (
  values: FieldValues
): FieldValues => {
  const preparedValues: FieldValues = {};
  for (const [key, value] of Object.entries(values)) {
    if (!isEmpty(value)) {
      preparedValues[key] = value;
    }
  }
  return preparedValues;
}

export const areValuesDifferent = (
  values1: FieldValues,
  values2: FieldValues = {}
): boolean => {
  const preparedValues1 = prepareValues(values1);
  const preparedValues2 = prepareValues(values2);
  return !isEqual(preparedValues1, preparedValues2);
};

export const isEmpty = (
  value: FieldValue
): boolean => {
  return (typeof value === "object" && value && !Object.keys(value as Object).length) ||
    EMPTY_VALUES.includes(value as (typeof EMPTY_VALUES[number]));
}

/**
 * Keeps only the named fields that carry a value.
 *
 * `undefined` is dropped rather than kept: it is how a field says it has nothing to give — a
 * direction it declines to be written in, or a request that never mentioned it. Keeping it
 * would put an attribute with no value into a database update statement, which DynamoDB
 * rejects outright rather than ignoring. `null` is a value and stays.
 *
 * @param values - The values to narrow.
 * @param names - The fields to keep.
 * @returns The named fields that have a value.
 */
export const pickFields = (values: FieldValues, names: FieldNames): FieldValues => {
  const picked: FieldValues = {};
  for (const name of names) {
    if (name in values && values[name] !== undefined) {
      picked[name] = values[name];
    }
  }
  return picked;
}

export const isDynamicStructure = <T, F extends FieldNames>(
  value: DynamicPropertyType<T, F>
): value is DynamicStructure<T, F> => {
  return typeof value === "object" &&
    value !== null &&
    "getter" in value &&
    typeof value.getter === "function";
}


  /**
   * Helper to prepare a property of the state generator,
   * which can be a static value or a dynamic structure, and return the result.
   *
   * @template T - The type of the property value.
   * @param property - The property to prepare.
   * @returns Prepared property as a dynamic structure.
   */
  export const prepareDynamicPropertyValue = <T>(
    property: DynamicPropertyType<T>
  ): PreparedDynamicStructure<T> =>{
    if (isDynamicStructure(property)) {
      property.fields = property.fields ?? [];
      return property as PreparedDynamicStructure<T>;
    }
    if (typeof property === "function") {
      return {
        getter: property as DynamicStructureGetter<T>,
        fields: [],
        disabledCache: true,
      }
    }
    return {
      getter: () => {
        return property;
      },
      fields: []
    }
  }
